import React, { useEffect, useState } from 'react';
import { GridComponent, ColumnsDirective, ColumnDirective, Resize, Sort, ContextMenu, Filter, Page, ExcelExport, PdfExport, Edit, Inject } from '@syncfusion/ej2-react-grids';
import Swal from 'sweetalert2';
import { Header } from '../components';
import { MdDeleteForever, MdEditNote } from "react-icons/md";

const Galleries = () => {
  const [galleriesData, setGalleriesData] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentEditGallery, setCurrentEditGallery] = useState(null);
  const [newGallery, setNewGallery] = useState({
    title: '',
    image_url: '',
    _id: null
  });

  useEffect(() => {
    fetchGalleries();
  }, []);

  const fetchGalleries = async () => {
    try {
      if (!process.env.REACT_APP_API_BASE_URL || !process.env.REACT_APP_API_KEY) {
        throw new Error('API configuration missing');
      }
      const response = await fetch(process.env.REACT_APP_API_BASE_URL + '/api/galleries', {
        headers: {
          'api-key': process.env.REACT_APP_API_KEY
        }
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      if (!Array.isArray(data)) {
        throw new Error('Invalid data format received');
      }

      const reversedData = [...data].reverse();
      setGalleriesData(reversedData);
    } catch (error) {
      console.error('Error fetching Galleries :', error);
      Swal.fire({
        title: 'Error!',
        text: 'Failed to fetch Galleries',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    }
  };
  const handleEditGallery = (gallery) => {
    setIsEditMode(true);
    setShowAddForm(true);

    setNewGallery({
      title: gallery.title,
      image_url: gallery.image_url,
      _id: gallery._id
    });
    setCurrentEditGallery(gallery);
  };

  const handleAddGallery = async (e) => {
    e.preventDefault();

    try {
      // Validate input fields
      if (!newGallery.title?.trim() || (!newGallery.image && !isEditMode)) {
        await Swal.fire({
          title: 'Error!',
          text: 'All fields are required',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#d33',
        });
        return;
      }

      // Check API configuration
      if (!process.env.REACT_APP_API_BASE_URL || !process.env.REACT_APP_API_KEY) {
        throw new Error('API configuration is missing');
      }

      let uploadedImageName = newGallery.image_url;

      // Step 1: If editing and a new file is selected, delete the old file
      if (isEditMode && newGallery.image && newGallery.image_url) {

        const imageFilename = formatImageFilename(newGallery.image_url);
        const deleteResponse = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/upload/${imageFilename}`, // Endpoint to delete the old file
          {
            method: 'DELETE',
            headers: {
              'api-key': process.env.REACT_APP_API_KEY,
            },
          }
        );

        if (!deleteResponse.ok) {
          const errorData = await deleteResponse.json().catch(() => null);
          throw new Error(errorData?.message || 'Failed to delete previous Image');
        }
      }

      // Step 2: Upload the new image file if a new one is selected
      if (newGallery.image) {
        const imageFile = newGallery.image; // Assuming `newGallery.image` contains the file object
        const formData = new FormData();
        formData.append('file', imageFile);

        const uploadResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/upload`, {
          method: 'POST',
          body: formData,
        });

        if (!uploadResponse.ok) {
          const errorData = await uploadResponse.json().catch(() => null);
          throw new Error(errorData?.message || 'Failed to upload Image');
        }

        const uploadResult = await uploadResponse.json();
        uploadedImageName = uploadResult.fileUrl; // Assuming the API returns the file name
      }

      // Step 3: Prepare gallery data

      const galleryData = {
        title: newGallery.title,
        image_url: uploadedImageName, // Store only the new image file name
      };

      // Step 4: Add or update gallery
      const method = isEditMode ? 'PUT' : 'POST';
      const url = isEditMode
        ? `${process.env.REACT_APP_API_BASE_URL}/api/galleries/${newGallery._id}`
        : `${process.env.REACT_APP_API_BASE_URL}/api/galleries`;

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'api-key': process.env.REACT_APP_API_KEY,
        },
        body: JSON.stringify(galleryData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || 'Failed to add/update gallery');
      }

      // Success handling
      await Swal.fire({
        title: 'Success!',
        text: isEditMode ? 'Gallery updated successfully' : 'Gallery added successfully',
        icon: 'success',
        confirmButtonText: 'OK',
        confirmButtonColor: '#3085d6',
      });

      // Reset form and refresh data
      setShowAddForm(false);
      setIsEditMode(false);
      setNewGallery({ title: '', image: null });
      await fetchGalleries();
    } catch (error) {
      console.error('Error adding/updating gallery:', error);

      // Error handling
      await Swal.fire({
        title: 'Error!',
        text: error.message || 'An unexpected error occurred',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#d33',
      });
    }
  };


  // Delete the gallery
  function formatImageFilename(cloudinaryUrl) {
    const regex = /\/image\/upload\/v\d+\/(uploads\/[^\/]+)(?:\.[a-z]+)$/;
    const match = cloudinaryUrl.match(regex);

    if (match && match[1]) {
      return match[1]; // Return path without file extension
    }

    return '';
  }


  const handleDelete = async (id, imageUrl) => {
    try {


      // Check for API configuration
      if (!process.env.REACT_APP_API_BASE_URL || !process.env.REACT_APP_API_KEY) {
        throw new Error("API configuration missing");
      }

      // Delete the gallery
      const galleryResponse = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/galleries/${id}`,
        {
          method: "DELETE",
          headers: {
            "api-key": process.env.REACT_APP_API_KEY,
          },
        }
      );

      if (!galleryResponse.ok) {
        throw new Error("Failed to delete gallery");
      }

      // If gallery deletion succeeds, delete the associated Image file
      if (imageUrl) {
        const imageFilename = formatImageFilename(imageUrl);
        const uploadResponse = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/upload/${imageFilename}`,
          {
            method: "DELETE",
            headers: {
              "api-key": process.env.REACT_APP_API_KEY,
            },
          }
        );

        if (!uploadResponse.ok) {
          throw new Error("Failed to delete associated Image file");
        }
      }

      // Refresh the galleries list
      await fetchGalleries();
      return true;
    } catch (error) {
      console.error("Error deleting gallery or associated file:", error);
      throw error;
    }
  };


  const galleriesGrid = [
    {
      field: 'title',
      headerText: 'Title',
      width: '150',
      textAlign: 'Center',
    },
    {
      field: 'image_url',
      headerText: 'Image',
      textAlign: 'Center',
      width: '150',
      template: (props) => (
        <div>
          {props.image_url && (
            <img
              className="rounded-xl h-20 md:ml-3"
              src={props.image_url}
              alt="order-item"
            />
          )}
        </div>
      )
    },
    {
      field: 'createdAt',
      headerText: 'Created At',
      width: '120',
      textAlign: 'Center',
    },
    {
      field: '_id',
      headerText: 'Actions',
      width: '150',
      textAlign: 'Center',
      template: (props) => (
        <div>
          <button
            className="bg-blue-500 text-white px-1 py-1 text-xl rounded mr-2"
            onClick={() => handleEditGallery(props)}
          >
            <MdEditNote />
          </button>
          <button
            className="bg-red-500 text-white px-1 py-1 text-xl rounded"
            onClick={() => {
              Swal.fire({
                title: 'Are you sure?',
                text: "You won't be able to revert this!",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes, delete it!',
              }).then(async (result) => {
                if (result.isConfirmed) {
                  try {
                    await handleDelete(props._id, props.image_url);

                    Swal.fire({
                      title: 'Deleted!',
                      text: 'Gallery has been deleted.',
                      icon: 'success',
                      confirmButtonText: 'OK',
                      confirmButtonColor: '#3085d6',
                    });
                  } catch (error) {
                    Swal.fire({
                      title: 'Error!',
                      text: 'Failed to delete the gallery or associated file.',
                      icon: 'error',
                      confirmButtonText: 'OK',
                      confirmButtonColor: '#3085d6',
                    });
                  }
                }
              });
            }}
          >
            <MdDeleteForever />
          </button>
        </div>
      )
    },
  ];

  return (
    <div className="m-2 md:m-10 sm:mt-24 p-2 md:p-4 bg-white rounded-3xl">
      <div className="flex justify-between items-center mb-4">
        <Header category="Page" title="Gallery" />
        <button
          onClick={() => {
            setShowAddForm(true);
            setIsEditMode(false);
            setNewGallery({ title: '', image: null });
          }}
          className="bg-green-500 text-white px-3 py-2 rounded"
        >
          Add Image
        </button>
      </div>

      {showAddForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex justify-center items-center">
          <div className="bg-white p-8 rounded-xl shadow-2xl w-[600px]">
            <h2 className="text-2xl font-semibold mb-6 text-gray-800 border-b pb-2">
              {isEditMode ? 'Edit Gallery' : 'Add New Gallery'}
            </h2>
            <form onSubmit={handleAddGallery} className="space-y-6">
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">Title:</label>
                <input
                  type="text"
                  value={newGallery.title}
                  onChange={(e) => setNewGallery({ ...newGallery, title: e.target.value })}
                  className="border border-gray-300 p-3 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                  required
                  placeholder="Enter Gallery title"
                />
              </div>
              <div className="mb-6">
                <label className="block text-gray-700 text-sm font-bold mb-2">Image:</label>

                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files.length > 0) {
                      const file = e.target.files[0];

                      // Validate file size (max 400KB)
                      if (file.size > 400 * 1024) {
                        Swal.fire({
                          icon: "error",
                          title: "File Too Large",
                          text: "The selected file must be less than 400KB.",
                          confirmButtonText: "Okay",
                          confirmButtonColor: '#3085d6',
                        });
                        e.target.value = ""; // Reset the input field
                        return;
                      }

                      setNewGallery({
                        ...newGallery,
                        image: file, // Handle selected file
                      });
                    }
                  }}
                  className="border border-gray-300 p-2 rounded w-full focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  {...(isEditMode ? {} : { required: true })}
                />

                {/* Conditionally show the current file preview in edit mode */}
                {isEditMode && newGallery.image_url && (
                  <div className="text-sm text-gray-600 mt-2">
                    <p>Current file:</p>
                    <img
                      src={newGallery.image_url}
                      alt="Current Gallery"
                      className="mt-2 w-32 h-32 object-cover border"
                    />
                  </div>
                )}
              </div>
              <div className="flex justify-end space-x-4 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setIsEditMode(false);
                    setNewGallery({ title: '', image: null });
                  }}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2.5 rounded-lg transition duration-200 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2.5 rounded-lg transition duration-200 font-medium"
                >
                  {isEditMode ? 'Update Gallery' : 'Add Gallery'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <GridComponent
        id="gridcomp"
        dataSource={galleriesData}
        allowPaging
        allowSorting
      >
        <ColumnsDirective>
          {galleriesGrid.map((item, index) => (
            <ColumnDirective key={index} {...item} />
          ))}
        </ColumnsDirective>
        <Inject services={[Resize, Sort, ContextMenu, Filter, Page, ExcelExport, Edit, PdfExport]} />
      </GridComponent>
    </div>
  );
};

export default Galleries;